// Reading `POST /api/imports/statement-sample` into the four cases the zone
// picker renders.
//
// ADR 0039 D9.2. The server supplies FACTS -- a sample clock face, two ranges,
// and a circular spread in seconds -- and deliberately no verdict about which
// copy to show. That decision is here because it is a copy decision: whether a
// span of a few minutes is worth printing as a range is a question about the
// sentence, not about the file.
//
// NOTHING HERE PARSES A RAW STRING FOR ITS VALUE. `raw` is split on its first
// space to drop the date, which reformats nothing, and every arithmetic input is
// the server's `wall_clock`. Re-parsing `raw` would be a second implementation
// of the parser's five accepted timestamp formats, free to drift from it.

import type { components } from "../api/schema";

type StatementSampleResponse = components["schemas"]["StatementSampleResponse"];
type StatementClockFace = components["schemas"]["StatementClockFaceResponse"];
type StatementRange = components["schemas"]["StatementRangeResponse"];

/**
 * Below this, the range line is dropped and the copy says so.
 *
 * Fifteen minutes. CLIENT-SIDE because it is a copy threshold rather than a
 * fact about the file -- the server already supplies the fact (`spread_seconds`)
 * and stays out of the wording. The comparison is strictly `<`, so a statement
 * spanning exactly fifteen minutes still gets its range line.
 */
export const NARROW_SPREAD_SECONDS = 900;

/** Both ends of whatever span the New York line converts. */
export type StatementSpan = {
  readonly earliest: StatementClockFace;
  readonly latest: StatementClockFace;
};

/**
 * Which of the four cases this reading is, with the values that case renders.
 *
 * A tagged union rather than a flag set, so the component cannot render the
 * range line in a case that has no range: `bulk` only exists on the variant
 * whose copy prints it.
 */
export type StatementSampleReading =
  | { readonly kind: "unreadable" }
  | {
      /** One single time in the whole file. Nothing to compare it against. */
      readonly kind: "only-time";
      readonly sample: StatementClockFace;
      readonly newYork: StatementSpan;
    }
  | {
      /** A span, but too narrow to read as a range. No range line. */
      readonly kind: "narrow";
      readonly sample: StatementClockFace;
      readonly newYork: StatementSpan;
    }
  | {
      readonly kind: "wide";
      readonly sample: StatementClockFace;
      /** The 5th-95th percentile span -- the PRIMARY line. */
      readonly bulk: StatementRange;
      /**
       * The extremes, and only when they differ from the bulk span.
       *
       * `null` when both ends read the same as the bulk range: a secondary line
       * repeating the primary one adds nothing and invites the user to look for
       * a difference that is not there.
       */
      readonly extremes: StatementRange | null;
      readonly newYork: StatementSpan;
    };

function spanOf(range: StatementRange | null, sample: StatementClockFace): StatementSpan {
  // A file with no span still gets a New York line, converted from the one time
  // it has: that line is the only thing on the screen that moves with the
  // selection, and the single-time cases are exactly where the user has least
  // else to check against.
  if (range === null) return { earliest: sample, latest: sample };
  return { earliest: range.earliest, latest: range.latest };
}

function endsDiffer(left: StatementRange, right: StatementRange): boolean {
  return left.earliest.raw !== right.earliest.raw || left.latest.raw !== right.latest.raw;
}

/** Read a sample response into the case the picker renders. */
export function readStatementSample(
  response: StatementSampleResponse,
): StatementSampleReading {
  const sample = response.sample;
  // Every field nulls together -- they come out of one scan -- so a null sample
  // is the whole "we could not read this file" case and not a partial reading.
  if (sample === null || response.basis === null) return { kind: "unreadable" };

  const newYork = spanOf(response.bulk_range, sample);

  if (response.basis === "ONLY_TIME") return { kind: "only-time", sample, newYork };

  const spread = response.spread_seconds;
  const bulk = response.bulk_range;
  // A MID_RANGE basis means the scan found a spread, so `bulk` and `spread` are
  // both populated. Falling back to the no-range copy rather than trusting that
  // keeps a malformed body to a weaker sentence instead of a crash.
  if (bulk === null || spread === null || spread < NARROW_SPREAD_SECONDS) {
    return { kind: "narrow", sample, newYork };
  }

  const full = response.full_range;
  return {
    kind: "wide",
    sample,
    bulk,
    extremes: full !== null && endsDiffer(full, bulk) ? full : null,
    newYork,
  };
}

/**
 * The TIME portion of a raw clock face, dropped from its date at the first space.
 *
 * All five accepted timestamp formats are `<date> <time>`, so the split is safe
 * and reformats nothing -- which is the point, since the user is comparing these
 * against their own thinkorswim screen.
 *
 * The RANGE lines use this and the SAMPLE line does not: a range reading "from
 * 1/14/26 06:30:02 to 1/15/26 12:58:41" misleads, because it is a time-of-day
 * range across many days rather than a span between two moments. The sample
 * keeps its date so the user can find that exact fill.
 */
export function timeOfDayOf(raw: string): string {
  const firstSpace = raw.indexOf(" ");
  return firstSpace === -1 ? raw : raw.slice(firstSpace + 1);
}

/**
 * A converted New York `HH:MM:SS` rendered the way the approved copy reads.
 *
 * ADR 0039 D9.2 pins this line's wording, times included: "9:30:02 AM to
 * 3:58:41 PM". The verbatim rule governs the statement's OWN strings, which are
 * shown untouched; this value is computed by the browser and has no raw form to
 * preserve.
 */
export function formatNewYorkClock(time: string): string {
  const [hour, minute, second] = time.split(":");
  const hour24 = Number(hour);
  const suffix = hour24 < 12 ? "AM" : "PM";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${minute ?? "00"}:${second ?? "00"} ${suffix}`;
}
